import React, { useEffect, useState } from 'react';
import { Card, List, Avatar, Typography, Spin, Empty, Button, message, Row, Col, Space, Modal, Divider, Badge, Collapse } from 'antd';
import { collection, query, where, orderBy, getDocs, updateDoc, doc, writeBatch, getDoc } from 'firebase/firestore';
import { useDispatch, useSelector } from 'react-redux';
import { db } from '../../data/base';
import { secureGetItem } from '../../data/utils';
import { fetchNotifications, markNotificationAsReadThunk, markAllNotificationsAsReadThunk } from '../../redux/notifications/actions';
import { statusTokens } from '../../theme/theme';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftOutlined, CaretRightOutlined, CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import NoteViewer from '../modal/NoteViewerModal';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import HeaderComponent from '../../components/Components/Headers/HeaderComponent';

const { Title, Text } = Typography;
const { Panel } = Collapse;

/**
 * Componente principale per la gestione e visualizzazione delle notifiche
 * Raggruppa le notifiche per tipo e permette di gestire lo stato di lettura
 */
const Notifications = () => {
    // === REDUX ===
    const dispatch = useDispatch();
    const notifications = useSelector(state => state.notifications.notifications);
    const loading = useSelector(state => state.notifications.loading);

    // === STATI DEL COMPONENTE ===

    /** Notifiche raggruppate per tipo (es. "Variant validated", "Variant declined", ecc.) */
    const [groupedNotifications, setGroupedNotifications] = useState({});

    /** Stato per il caricamento durante l'operazione "segna tutto come letto" */
    const [markingAsRead, setMarkingAsRead] = useState(false);

    /** Stato per tenere traccia di quali gruppi di notifiche sono collassati */
    const [collapsedGroups, setCollapsedGroups] = useState({});

    /** Mappa per memorizzare i nomi dei brand (brandId -> nome brand) */
    const [brandNames, setBrandNames] = useState({});

    /** Mappa per memorizzare i nomi dei profili (email -> nome completo) */
    const [profileNames, setProfileNames] = useState({});

    // === VARIABILI E RIFERIMENTI ===

    /** Email dell'utente corrente ottenuta dal localStorage sicuro */
    const email = secureGetItem("email");

    /** Hook per la navigazione tra le pagine */
    const navigate = useNavigate();

    /** Hook per accedere allo state della navigazione */
    const location = useLocation();

    /** Riferimenti per ogni gruppo di notifiche per permettere lo scroll automatico */
    const groupRefs = React.useRef({});

    // === EFFETTI ===

    /**
     * Effetto che si esegue al montaggio del componente
     * Carica le notifiche iniziali
     */
    useEffect(() => {
        if (email) {
            dispatch(fetchNotifications(email));
        }
    }, [email, dispatch]);

    // === FUNZIONI DI RECUPERO DATI ===

    /**
     * Recupera i nomi dei brand da Firestore basandosi sui brandId
     * @param {string[]} brandIds - Array di ID dei brand da recuperare
     */
    const fetchBrandNames = async (brandIds) => {
        const uniqueBrandIds = [...new Set(brandIds)]; // Rimuove duplicati
        const brandNamesMap = {};

        try {
            // Recupera i documenti dei brand dalla collezione MainBrands
            const brandPromises = uniqueBrandIds.map(async (brandId) => {
                if (!brandId) return null;

                try {
                    const brandDoc = await getDoc(doc(db, "MainBrands", brandId));
                    if (brandDoc.exists()) {
                        const brandData = brandDoc.data();
                        return {
                            id: brandId,
                            name: brandData.nameBrand || brandId // Fallback all'ID se nameBrand non esiste
                        };
                    }
                    return { id: brandId, name: brandId }; // Fallback all'ID se il documento non esiste
                } catch (error) {
                    console.error(`Errore nel recupero del brand ${brandId}:`, error);
                    return { id: brandId, name: brandId }; // Fallback all'ID in caso di errore
                }
            });

            const brandResults = await Promise.all(brandPromises);

            // Costruisce la mappa brandId -> nome brand
            brandResults.forEach(result => {
                if (result) {
                    brandNamesMap[result.id] = result.name;
                }
            });

            setBrandNames(brandNamesMap);
        } catch (error) {
            console.error("Errore nel recupero dei nomi dei brand:", error);
        }
    };

    /**
     * Recupera i nomi completi dei profili da Firestore basandosi sulle email
     * @param {string[]} emails - Array di email dei profili da recuperare
     */
    const fetchProfileNames = async (emails) => {
        const uniqueEmails = [...new Set(emails)]; // Rimuove duplicati
        const profileNamesMap = {};

        try {
            // Recupera i documenti dei profili dalla collezione Profiles
            const profilePromises = uniqueEmails.map(async (email) => {
                if (!email) return null;

                try {
                    const profileDoc = await getDoc(doc(db, "Profiles", email));
                    if (profileDoc.exists()) {
                        const profileData = profileDoc.data();
                        const firstName = profileData.firstName || '';
                        const lastName = profileData.lastName || '';
                        const fullName = `${firstName} ${lastName}`.trim();
                        return {
                            email: email,
                            name: fullName || email // Fallback all'email se nome non disponibile
                        };
                    }
                    return { email: email, name: email }; // Fallback all'email se il documento non esiste
                } catch (error) {
                    console.error(`Errore nel recupero del profilo ${email}:`, error);
                    return { email: email, name: email }; // Fallback all'email in caso di errore
                }
            });

            const profileResults = await Promise.all(profilePromises);

            // Costruisce la mappa email -> nome completo
            profileResults.forEach(result => {
                if (result) {
                    profileNamesMap[result.email] = result.name;
                }
            });

            setProfileNames(profileNamesMap);
        } catch (error) {
            console.error("Errore nel recupero dei nomi dei profili:", error);
        }
    };

    // Effetto per aggiornare le notifiche raggruppate quando cambiano le notifiche Redux
    useEffect(() => {
        if (notifications.length > 0) {
            // Raggruppa le notifiche per tipo
            const grouped = groupNotificationsByType(notifications);
            setGroupedNotifications(grouped);

            // Imposta tutti i gruppi come collassati inizialmente, eccetto quello specificato nello state
            const initialCollapsed = initializeCollapsedGroups(grouped);
            const openGroup = location.state?.openGroup;
            if (openGroup && grouped[openGroup]) {
                initialCollapsed[openGroup] = false; // Apri il gruppo specificato
            }
            setCollapsedGroups(initialCollapsed);

            // Recupera i nomi dei brand per le notifiche che hanno brandId
            const brandIds = notifications
                .filter(notification => notification.brandId)
                .map(notification => notification.brandId);

            if (brandIds.length > 0) {
                fetchBrandNames(brandIds);
            }

            // Recupera i nomi dei profili per le notifiche che hanno sentBy o sentTo
            const emails = [];
            notifications.forEach(notification => {
                if (notification.sentBy && !emails.includes(notification.sentBy)) {
                    emails.push(notification.sentBy);
                }
                if (notification.sentTo && !emails.includes(notification.sentTo)) {
                    emails.push(notification.sentTo);
                }
            });

            if (emails.length > 0) {
                fetchProfileNames(emails);
            }

            // Scroll automatico al gruppo specificato nello state dopo un breve ritardo
            if (openGroup && grouped[openGroup]) {
                setTimeout(() => {
                    scrollToNotificationGroup(openGroup);
                }, 500);
            }
        }
    }, [notifications, location.state?.openGroup]);

    // === FUNZIONI DI UTILITÀ ===

    /**
     * Raggruppa un array di notifiche per tipo
     * @param {Array} notifications - Array di notifiche da raggruppare
     * @returns {Object} Oggetto con chiavi = tipi di notifica, valori = array di notifiche
     */
    const groupNotificationsByType = (notifications) => {
        const grouped = {};

        notifications.forEach(notification => {
            const type = notification.type || "Unknown";
            if (!grouped[type]) {
                grouped[type] = [];
            }
            grouped[type].push(notification);
        });

        return grouped;
    };

    /**
     * Inizializza lo stato di collasso per tutti i gruppi di notifiche
     * @param {Object} groupedNotifs - Notifiche raggruppate per tipo
     * @returns {Object} Oggetto con chiavi = tipi di notifica, valori = boolean (true = collassato)
     */
    const initializeCollapsedGroups = (groupedNotifs) => {
        const collapsed = {};
        Object.keys(groupedNotifs).forEach(type => {
            collapsed[type] = true; // Imposta tutti i gruppi come collassati (true)
        });
        return collapsed;
    };

    // === GESTORI DI EVENTI ===

    /**
     * Gestisce il click su una notifica
     * Segna la notifica come letta e naviga alla pagina di dettaglio se possibile
     * @param {Object} notification - Oggetto notifica cliccata
     */
    const handleNotificationClick = async (notification) => {
        // Segna la notifica come letta se non lo è già
        if (!notification.read) {
            try {
                await dispatch(markNotificationAsReadThunk(notification.id));

                // Aggiorna anche i gruppi di notifiche
                setGroupedNotifications(prevGrouped => {
                    const newGrouped = { ...prevGrouped };
                    Object.keys(newGrouped).forEach(type => {
                        newGrouped[type] = newGrouped[type].map(n =>
                            n.id === notification.id ? { ...n, read: true } : n
                        );
                    });
                    return newGrouped;
                });
            } catch (error) {
                console.error("Errore nell'aggiornamento della notifica:", error);
            }
        }

        // Naviga al contenuto specifico della notifica
        if (notification.type === "Order - Add 3d assets to catalog") {
            // Per le notifiche di catalogo, naviga al CatalogueDetail specifico
            if (notification.catalogId) {
                navigate(`/my-catalogues/${notification.catalogId}`);
            } else {
                // Fallback alla pagina dei cataloghi se non c'è catalogId
                navigate("/my-catalogues");
            }
        } else if (notification.variantId && notification.brandId && notification.productId) {
            // Per le notifiche di varianti, naviga alla pagina di dettaglio della variante
            navigate(`/framevalidation/${notification.brandId}/${notification.productId}/${notification.variantId}`);
        }
    };

    /**
     * Visualizza i dettagli della nota di decline in una modale
     * @param {Event} e - Evento click per prevenire la propagazione
     * @param {Object} notification - Notifica contenente i dettagli della nota
     */
    const viewDeclineDetails = async (e, notification) => {
        e.stopPropagation(); // Previene la propagazione dell'evento al parent (la riga della notifica)

        // Usa declineRef se disponibile, altrimenti prova con declineDescription
        const noteId = notification.declineRef || notification.declineDescription;

        if (!noteId) {
            message.error("Nessuna nota di decline disponibile");
            return;
        }

        try {
            // Crea un oggetto record fittizio per il NoteViewer
            const record = {
                glassesName: notification.nameVariant || "",
                skuModel: notification.skuModel || ""
            };

            // Usa il componente NoteViewer con l'ID della nota
            const noteViewerComponent = <NoteViewer
                list_notes={[noteId]}
                record={record}
            />;

            // Simula un click sul bottone del NoteViewer per aprire la modale
            const noteViewerElement = document.createElement('div');
            document.body.appendChild(noteViewerElement);

            const clickEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window
            });

            // Usa ReactDOM per renderizzare il componente
            const ReactDOM = await import('react-dom');
            ReactDOM.render(noteViewerComponent, noteViewerElement);

            // Simula il click dopo che il componente è stato renderizzato
            const button = noteViewerElement.querySelector('button');
            if (button) {
                button.dispatchEvent(clickEvent);
            }
        } catch (error) {
            console.error("Errore nell'apertura dei dettagli della nota:", error);
            message.error("Impossibile visualizzare i dettagli della nota");
        }
    };

    // === FUNZIONI DI FORMATTAZIONE ===

    /**
     * Formatta una data per la visualizzazione nelle notifiche
     * @param {Date} date - Data da formattare
     * @returns {string} Data formattata come stringa
     */
    const formatNotificationDate = (date) => {
        if (!date) return "";

        const dateObj = new Date(date);
        return dateObj.toLocaleString('en-US', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    /**
     * Ottiene informazioni di stile (colore, icona, titolo) per un tipo di notifica
     * @param {string} type - Tipo di notifica
     * @returns {Object} Oggetto contenente color, icon, title
     */
    const getNotificationStyleInfo = (type) => {
        switch (type) {
            case "Variant validated":
                return {
                    color: statusTokens.success,
                    icon: 'V',
                    title: 'Variant Validated'
                };
            case "Variant declined":
                return {
                    color: statusTokens.error,
                    icon: 'X',
                    title: 'Variant Declined'
                };
            case "Variant assigned":
                return {
                    color: "#1890ff",
                    icon: 'A',
                    title: 'Variant Assigned'
                };
            case "Order - Add 3d assets to catalog":
                return {
                    color: "#722ed1",
                    icon: 'O',
                    title: 'My Catalogue'
                };
            default:
                return {
                    color: statusTokens.info,
                    icon: 'N',
                    title: 'Notification'
                };
        }
    };

    // === FUNZIONI DI AZIONE ===

    /**
     * Segna tutte le notifiche come lette utilizzando una transazione batch
     */
    const markAllAsRead = async () => {
        if (notifications.length === 0) return;

        try {
            setMarkingAsRead(true);

            // Filtra solo le notifiche non lette
            const unreadNotifications = notifications.filter(notification => !notification.read);

            if (unreadNotifications.length === 0) {
                message.info('All notifications are already read');
                setMarkingAsRead(false);
                return;
            }

            // Usa Redux per segnare tutte le notifiche come lette
            await dispatch(markAllNotificationsAsReadThunk(notifications));

            // Aggiorna i gruppi di notifiche
            setGroupedNotifications(prevGrouped => {
                const newGrouped = { ...prevGrouped };
                Object.keys(newGrouped).forEach(type => {
                    newGrouped[type] = newGrouped[type].map(notification => ({ ...notification, read: true }));
                });
                return newGrouped;
            });

            message.success(`Marked ${unreadNotifications.length} notifications as read`);
        } catch (error) {
            console.error("Error marking notifications as read:", error);
            message.error("Failed to mark notifications as read");
        } finally {
            setMarkingAsRead(false);
        }
    };

    /**
     * Conta le notifiche non lette in un array di notifiche
     * @param {Array} notificationsArray - Array di notifiche
     * @returns {number} Numero di notifiche non lette
     */
    const countUnreadByType = (notificationsArray) => {
        return notificationsArray.filter(n => !n.read).length;
    };

    /**
     * Gestisce l'espansione/compressione di un gruppo di notifiche
     * @param {string} groupType - Tipo di gruppo da espandere/comprimere
     */
    const toggleGroupCollapse = (groupType) => {
        setCollapsedGroups(prev => ({
            ...prev,
            [groupType]: !prev[groupType]
        }));
    };

    /**
     * Scrolla automaticamente a un gruppo di notifiche specifico
     * @param {string} groupType - Tipo di gruppo a cui scorrere
     */
    const scrollToNotificationGroup = (groupType) => {
        // Assicuriamoci che il gruppo sia espanso
        setCollapsedGroups(prev => ({
            ...prev,
            [groupType]: false // Imposta a false per espandere il gruppo
        }));

        // Scorri alla sezione dopo un breve ritardo per assicurarsi che il gruppo sia espanso
        setTimeout(() => {
            if (groupRefs.current[groupType]) {
                groupRefs.current[groupType].scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    // === FUNZIONI DI RENDERING ===

    /**
     * Renderizza le notifiche raggruppate per tipo
     * @returns {JSX.Element} Componente JSX con le notifiche raggruppate
     */
    const renderGroupedNotifications = () => {
        // Se non ci sono notifiche, mostra un messaggio vuoto
        if (Object.keys(groupedNotifications).length === 0) {
            return (
                <Empty
                    description="No notifications"
                    style={{ padding: '40px 0' }}
                />
            );
        }

        return (
            <div className="notification-groups">
                {Object.keys(groupedNotifications).map((type, index) => {
                    const notifs = groupedNotifications[type];
                    const { color, icon, title } = getNotificationStyleInfo(type);
                    const unreadCount = countUnreadByType(notifs);
                    const isCollapsed = collapsedGroups[type] || false;

                    // Se il gruppo è vuoto, non renderizzarlo
                    if (notifs.length === 0) return null;

                    return (
                        <Card
                            key={`group-${index}`}
                            ref={el => groupRefs.current[type] = el}
                            style={{
                                marginBottom: '20px',
                                borderLeft: `3px solid ${color}`,
                                backgroundColor: isCollapsed ? `${color}10` : '#ffffff'
                            }}
                            styles={{ body: { padding: 0 } }}
                        >
                            {/* Header del gruppo con titolo, contatore e controlli di espansione */}
                            <div style={{
                                padding: '12px 16px',
                                backgroundColor: `${color}20`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                border: `1px solid ${color}30`
                            }}
                                onClick={() => toggleGroupCollapse(type)}
                            >
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar shape="circle" style={{
                                        backgroundColor: color,
                                        marginRight: '12px',
                                        width: '32px',
                                        height: '32px',
                                        lineHeight: '32px',
                                        fontSize: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 'bold'
                                    }}>
                                        {icon}
                                    </Avatar>
                                    <Title level={4} style={{ margin: 0 }}>{title}</Title>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    {/* Badge con il numero di notifiche non lette */}
                                    {unreadCount > 0 && (
                                        <Badge
                                            count={unreadCount}
                                            style={{ backgroundColor: color }}
                                            overflowCount={99}
                                        />
                                    )}
                                    {/* Icona per indicare se il gruppo è espanso o collassato */}
                                    {isCollapsed ? (
                                        <CaretDownOutlined style={{ fontSize: '16px', color: '#666' }} />
                                    ) : (
                                        <CaretUpOutlined style={{ fontSize: '16px', color: '#666' }} />
                                    )}
                                </div>
                            </div>
                            {/* Lista delle notifiche (visibile solo se il gruppo è espanso) */}
                            {!isCollapsed && (
                                <List
                                    itemLayout="horizontal"
                                    dataSource={notifs}
                                    renderItem={(item) => (
                                        <List.Item
                                            onClick={() => handleNotificationClick(item)}
                                            style={{
                                                cursor: 'pointer',
                                                padding: '16px',
                                                backgroundColor: item.read ? 'transparent' : `${color}05`,
                                                borderBottom: '1px solid #f0f0f0',
                                                position: 'relative'
                                            }}
                                            className="notification-item"
                                        >
                                            {/* Indicatore visivo per notifiche non lette */}
                                            {!item.read && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        left: '0',
                                                        top: '0',
                                                        bottom: '0',
                                                        width: '4px',
                                                        backgroundColor: color,
                                                    }}
                                                />
                                            )}
                                            <List.Item.Meta
                                                title={
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }}>
                                                        <span style={{
                                                            fontWeight: item.read ? 'normal' : 'bold',
                                                            fontSize: '16px'
                                                        }}>
                                                            {/* Titolo della notifica basato sul tipo */}
                                                            {item.type === "Order - Add 3d assets to catalog" ? (item.nameCatalog || 'N/A') : (item.nameVariant || 'N/A')}
                                                            {/* Badge "NEW" per notifiche non lette */}
                                                            {!item.read && (
                                                                <span style={{
                                                                    marginLeft: '8px',
                                                                    fontSize: '12px',
                                                                    padding: '1px 8px',
                                                                    borderRadius: '10px',
                                                                    backgroundColor: color,
                                                                    color: '#fff',
                                                                    fontWeight: 'normal',
                                                                    verticalAlign: 'middle'
                                                                }}>
                                                                    NEW
                                                                </span>
                                                            )}
                                                        </span>
                                                        {/* Data e ora della notifica */}
                                                        <span style={{
                                                            fontSize: '14px',
                                                            color: item.read ? '#999' : '#555',
                                                            backgroundColor: item.read ? `${color}10` : `${color}20`,
                                                            padding: '3px 10px',
                                                            borderRadius: '4px',
                                                            fontWeight: item.read ? 'normal' : 'bold'
                                                        }}>
                                                            {formatNotificationDate(item.date)}
                                                        </span>
                                                    </div>
                                                }
                                                description={
                                                    <div style={{
                                                        color: item.read ? '#666' : '#444',
                                                        marginTop: '8px',
                                                        fontSize: '14px'
                                                    }}>
                                                        {/* Informazioni sulla direzione e mittente/destinatario */}
                                                        <div style={{ marginBottom: '8px' }}>
                                                            {item.notificationDirection === 'sent' ? (
                                                                <>
                                                                    <strong>Sent to:</strong> {profileNames[item.sentTo] || item.sentTo || 'Unknown'}
                                                                    <span style={{
                                                                        marginLeft: '12px',
                                                                        fontSize: '12px',
                                                                        padding: '2px 8px',
                                                                        backgroundColor: '#e6f7ff',
                                                                        color: '#1890ff',
                                                                        borderRadius: '4px',
                                                                        fontWeight: 'bold'
                                                                    }}>
                                                                        SENT
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <strong>From:</strong> {profileNames[item.sentBy] || item.sentBy || 'System'}
                                                                    <span style={{
                                                                        marginLeft: '12px',
                                                                        fontSize: '12px',
                                                                        padding: '2px 8px',
                                                                        backgroundColor: '#f6ffed',
                                                                        color: '#52c41a',
                                                                        borderRadius: '4px',
                                                                        fontWeight: 'bold'
                                                                    }}>
                                                                        RECEIVED
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>
                                                        {/* Dettagli specifici per tipo di notifica */}
                                                        <Row gutter={[16, 8]}>
                                                            {item.type === "Order - Add 3d assets to catalog" ? (
                                                                <>
                                                                    <Col span={12}>
                                                                        <strong>Catalog Name:</strong> {item.nameCatalog || 'N/A'}
                                                                    </Col>
                                                                    <Col span={12}>
                                                                        <strong>Number of Assets:</strong> {item.numberAssets || 'N/A'}
                                                                    </Col>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Col span={12}>
                                                                        <strong>SKU Model:</strong> {item.skuModel || 'N/A'}
                                                                    </Col>
                                                                    <Col span={12}>
                                                                        <strong>Brand:</strong> {brandNames[item.brandId] || item.brandId || 'N/A'}
                                                                    </Col>
                                                                    {item.newStatus && (
                                                                        <Col span={12}>
                                                                            <strong>Status:</strong> {item.newStatus}
                                                                        </Col>
                                                                    )}
                                                                </>
                                                            )}
                                                            {/* Link per visualizzare le note di decline */}
                                                            {(item.declineRef || item.declineDescription) && (
                                                                <Col span={24}>
                                                                    <div style={{ color: statusTokens.error }}>
                                                                        <strong>Notes:</strong>{' '}
                                                                        <a
                                                                            onClick={(e) => viewDeclineDetails(e, item)}
                                                                            style={{ color: statusTokens.error, textDecoration: 'underline', cursor: 'pointer' }}
                                                                        >
                                                                            View details
                                                                        </a>
                                                                    </div>
                                                                </Col>
                                                            )}
                                                        </Row>
                                                        {/* Messaggi di default per ogni tipo di notifica */}
                                                        {item.type === "Variant validated" && !item.message && (
                                                            <div style={{ marginTop: '8px', color: statusTokens.success }}>
                                                                Variant awaiting your approval
                                                            </div>
                                                        )}
                                                        {item.type === "Variant declined" && !item.message && !item.declineRef && !item.declineDescription && (
                                                            <div style={{ marginTop: '8px', color: statusTokens.error }}>
                                                                The variant has been rejected
                                                            </div>
                                                        )}
                                                        {item.type === "Variant assigned" && !item.message && (
                                                            <div style={{ marginTop: '8px', color: "#1890ff" }}>
                                                                The variant has been assigned
                                                            </div>
                                                        )}
                                                        {item.type === "Order - Add 3d assets to catalog" && !item.message && (
                                                            <div style={{ marginTop: '8px', color: "#722ed1" }}>
                                                                Order processing completed
                                                            </div>
                                                        )}
                                                        {/* Messaggio personalizzato se presente */}
                                                        {item.message && (
                                                            <div style={{ marginTop: '8px' }}>
                                                                <strong>Message:</strong> {item.message}
                                                            </div>
                                                        )}
                                                    </div>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            )}
                        </Card>
                    );
                })}
            </div>
        );
    };

    // === RENDERING PRINCIPALE ===
    return (
        <div>
            {/* === SEZIONE HEADER === */}
            <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', height: '64px' }}>
                        {/* Pulsante Back per tornare alla pagina precedente */}
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div>
                                <HeaderComponent
                                    buttonIcon={<NavigateBeforeIcon style={{ fontSize: '1.5rem' }} />}
                                    buttonText=""
                                    onButtonClick={() => navigate(-1)}
                                    isVisible={true}
                                    buttonPosition="left"
                                    noCamp={true}
                                />
                            </div>
                        </div>
                        {/* Titolo della pagina */}
                        <HeaderComponent
                            title="Notifications"
                            isVisible={false}
                            useSpaceBetween={false}
                            noCamp={true}
                        />
                    </div>

                    {/* Bottoni di azione: Mark all as read e Refresh */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Space>
                            <Button
                                type="primary"
                                onClick={markAllAsRead}
                                loading={markingAsRead}
                                disabled={loading || notifications.length === 0 || notifications.every(n => n.read)}
                            >
                                Mark all as read
                            </Button>
                            <Button
                                onClick={() => {
                                    if (email) {
                                        dispatch(fetchNotifications(email));
                                    }
                                }}
                                loading={loading}
                            >
                                Refresh
                            </Button>
                        </Space>
                    </div>
                </div>
            </div>

            {/* === CONTENUTO PRINCIPALE === */}
            {loading ? (
                // Spinner di caricamento
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spin size="large" />
                </div>
            ) : (
                <>
                    {/* === RIASSUNTO DELLE NOTIFICHE NON LETTE === */}
                    {(() => {
                        // Calcola il riassunto delle notifiche non lette per tipo
                        const unreadSummary = Object.entries(groupedNotifications)
                            .map(([type, notifs]) => {
                                const count = notifs.filter(n => !n.read).length;
                                return { type, count };
                            })
                            .filter(item => item.count > 0);

                        const totalUnread = unreadSummary.reduce((acc, curr) => acc + curr.count, 0);

                        if (totalUnread > 0) {
                            // Layout semplificato per un solo tipo di notifica non letta
                            if (unreadSummary.length === 1) {
                                const { type, count } = unreadSummary[0];
                                const { color, icon, title } = getNotificationStyleInfo(type);

                                return (
                                    <div style={{
                                        marginBottom: '20px',
                                        padding: '12px 16px',
                                        backgroundColor: `${color}10`,
                                        borderRadius: '4px',
                                        border: `1px solid ${color}30`,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <Title level={5} style={{ margin: 0 }}>
                                            You have {count} unread {title.toLowerCase()}
                                        </Title>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                backgroundColor: `${color}15`,
                                                padding: '5px 12px',
                                                borderRadius: '4px',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => scrollToNotificationGroup(type)}
                                        >
                                            <Avatar
                                                shape="circle"
                                                size="small"
                                                style={{
                                                    backgroundColor: color,
                                                    marginRight: '8px',
                                                    fontSize: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {icon}
                                            </Avatar>
                                            <span style={{ fontWeight: '500' }}>{count} {title}</span>
                                        </div>
                                    </div>
                                );
                            }

                            // Layout standard per più tipi di notifiche non lette
                            return (
                                <div style={{
                                    marginBottom: '20px',
                                    padding: '12px 16px',
                                    backgroundColor: '#f6f6f6',
                                    borderRadius: '4px',
                                    border: '1px solid #e8e8e8'
                                }}>
                                    <Row align="middle" gutter={[0, 12]}>
                                        <Col xs={24} md={12}>
                                            <Title level={5} style={{ margin: 0 }}>
                                                You have {totalUnread} unread notification{totalUnread !== 1 ? 's' : ''}
                                            </Title>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Row justify="end" gutter={[16, 8]}>
                                                {unreadSummary.map(({ type, count }, index) => {
                                                    const { color, icon, title } = getNotificationStyleInfo(type);
                                                    return (
                                                        <Col key={index}>
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                backgroundColor: `${color}15`,
                                                                padding: '5px 12px',
                                                                borderRadius: '4px'
                                                            }}>
                                                                <Avatar
                                                                    shape="circle"
                                                                    size="small"
                                                                    style={{
                                                                        backgroundColor: color,
                                                                        marginRight: '8px',
                                                                        fontSize: '12px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontWeight: 'bold'
                                                                    }}
                                                                >
                                                                    {icon}
                                                                </Avatar>
                                                                <span
                                                                    style={{ fontWeight: '500', cursor: 'pointer' }}
                                                                    onClick={() => scrollToNotificationGroup(type)}
                                                                >{count} {title}</span>
                                                            </div>
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        </Col>
                                    </Row>
                                </div>
                            );
                        }

                        return null;
                    })()}

                    {/* === NOTIFICHE RAGGRUPPATE === */}
                    {renderGroupedNotifications()}
                </>
            )}
        </div>
    );
};

export default Notifications;